// Tree shaping for the hierarchy builder.
//
// The tree endpoint may hand back either a nested structure (`children` on each
// node) or a flat list keyed by `parent_id`. Both are normalised here so the
// rendering code only ever sees one shape, and so the screen keeps working if
// the API changes its mind about which it returns.

const byOrder = (a, b) => {
  const orderA = a.sort_order ?? 0;
  const orderB = b.sort_order ?? 0;
  if (orderA !== orderB) return orderA - orderB;
  return (a.name || "").localeCompare(b.name || "");
};

const isNested = (nodes) =>
  Array.isArray(nodes) && nodes.some((node) => Array.isArray(node.children));

const sortDeep = (nodes) =>
  [...nodes].sort(byOrder).map((node) => ({
    ...node,
    children: sortDeep(node.children || []),
  }));

export const buildTree = (nodes) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  if (isNested(nodes)) return sortDeep(nodes);

  const byId = new Map();
  nodes.forEach((node) => byId.set(node.id, { ...node, children: [] }));

  const roots = [];
  byId.forEach((node) => {
    const parent = node.parent_id ? byId.get(node.parent_id) : null;
    // A node whose parent is missing from the payload is treated as a root
    // rather than dropped — losing a subtree silently is worse than showing it
    // at the wrong depth.
    if (parent) parent.children.push(node);
    else roots.push(node);
  });

  return sortDeep(roots);
};

// Depth-first walk in the order rows appear on screen, carrying each node's
// ancestor chain so the panel can render a breadcrumb without a second lookup.
export const walkTree = (roots, visit, trail = []) => {
  roots.forEach((node) => {
    visit(node, trail);
    walkTree(node.children || [], visit, [...trail, node]);
  });
};

export const findNode = (roots, nodeId) => {
  let found = null;
  walkTree(roots, (node) => {
    if (node.id === nodeId) found = node;
  });
  return found;
};

export const ancestorsOf = (roots, nodeId) => {
  let trail = [];
  walkTree(roots, (node, nodeTrail) => {
    if (node.id === nodeId) trail = nodeTrail;
  });
  return trail;
};

export const countDescendants = (node) => {
  let total = 0;
  walkTree(node.children || [], () => {
    total += 1;
  });
  return total;
};

// Guards the move: a node cannot become a child of itself or of anything
// beneath it. The server checks this too via the materialised path, but the
// drop target is disabled here so it never gets attempted.
export const isSelfOrDescendant = (node, candidateId) => {
  if (node.id === candidateId) return true;
  let hit = false;
  walkTree(node.children || [], (child) => {
    if (child.id === candidateId) hit = true;
  });
  return hit;
};

export const depthOf = (roots, nodeId) => ancestorsOf(roots, nodeId).length + 1;

export const totalNodes = (roots) => {
  let total = 0;
  walkTree(roots, () => {
    total += 1;
  });
  return total;
};

// Counts roll up the subtree, because "how many people sit under Engineering"
// is the question the grid and the department header actually ask.
export const rollUp = (node, field) => {
  let total = node[field] ?? 0;
  walkTree(node.children || [], (child) => {
    total += child[field] ?? 0;
  });
  return total;
};

export const rollUpAll = (roots, field) =>
  roots.reduce((sum, node) => sum + rollUp(node, field), 0);
